import { defaults, fromPairs } from 'lodash';
import uuid from 'uuid/v4';

/**
 * Modifies breakdown config at runtime, adding global, atlas-specific
 * transforms and business logic that can't be determined without the results
 * or certain contextual options. Includes things like:
 * - geo comparison
 * - color/label transforms associated with attributes (racethd, year, etc)
 * - rank chart labeling/sorting
 */

export default class BreakdownAugmenter {
  constructor(config) {
    this.breakdown = (config && JSON.parse(JSON.stringify(config))) || {};

    defaults(this.breakdown, {
      valueField: 'data_column',
      mouseoverField: 'mouseover_column_01',
      queryFilters: {},
      dynamicFields: [],
      combinations: [],
      contexts: [],
      statements: {},
      geoCodes: [],
      selectedFields: [],
      transforms: [],
      dimensions: [],
      axes: [],
    });
  }

  applyAttributeTransforms(attributes = []) {
    // TODO: attributes format has changed.
    const valuesByField = attributes.reduce(
      (acc, attribute) => ({
        ...acc,
        [attribute.field]: {
          ...(acc[attribute.field] || {}),
          field: attribute.field,
          colors: [
            ...((acc[attribute.field] || {}).colors || []),
            { key: attribute.value, value: attribute.color },
          ],
          labels: [
            ...((acc[attribute.field] || {}).labels || []),
            { key: attribute.value, value: attribute.label },
          ],
        },
      }),
      {},
    );

    const attributeTransforms = Object.values(valuesByField).reduce(
      (acc, values) => [
        ...acc,
        ...['colors', 'labels'].map((attribute) => ({
          id: uuid(),
          type: 'categorical',
          name: `${values.field} ${attribute}`,
          field: values.field,
          values: values[attribute],
          attribute,
        })),
      ],
      [],
    );

    const transformMap = fromPairs(
      attributeTransforms.map(({ id, field, attribute }) => [
        `${field}:${attribute}`,
        id,
      ]),
    );

    this.breakdown.transforms = this.breakdown.transforms.concat(
      attributeTransforms,
    );
    this.breakdown.transforms.push({
      id: uuid(),
      name: 'geo labels',
      type: 'identity',
      field: 'GEO_NAME',
      attribute: 'labels',
    });
    this.breakdown.dimensions = this.breakdown.dimensions.map(
      ({ dimension, transform, attribute, field }) => ({
        dimension,
        transform: attribute
          ? transformMap[`${field}:${attribute}`]
          : transform,
      }),
    );
  }

  bandByGeo() {
    const id = uuid();
    this.breakdown.transforms = this.breakdown.transforms.concat([
      {
        id,
        name: 'geo band',
        type: 'band',
        field: 'geo_code_long',
      },
    ]);

    this.breakdown.dimensions = this.breakdown.dimensions.concat([
      { dimension: 'band', transform: id },
    ]);
  }

  colorByGeoName() {
    const id = uuid();
    const dimensionIndex = this.breakdown.dimensions.findIndex(
      (d) => d.dimension === 'fill',
    );
    const dimension =
      dimensionIndex !== -1 ? this.breakdown.dimensions[dimensionIndex] : null;

    this.breakdown.transforms.push({
      id,
      type: 'scheme',
      scheme: ['#ff641e', '#b2b8be'],
      field: 'GEO_NAME',
      attribute: 'colors',
    });

    if (dimension) {
      this.breakdown.dimensions.splice(dimensionIndex, 1, {
        ...dimension,
        transform: id,
      });
    } else {
      this.breakdown.dimensions.push({ dimension: 'fill', transform: id });
    }
  }

  colorByGeoCode(primaryGeoCode, secondaryGeoCode) {
    const id = uuid();
    const dimension = this.breakdown.dimensions.find(
      (d) => d.dimension === 'fill',
    );

    this.breakdown.transforms.push({
      id,
      name: 'geo rank colors',
      type: 'categorical',
      values: [
        { key: primaryGeoCode, value: '#ee2b74' },
        {
          key: secondaryGeoCode,
          value:
            primaryGeoCode &&
            // TODO: do we still need this?
            ['03', '05'].includes(primaryGeoCode.substr(0, 2))
              ? '#ee2b74'
              : '#9098a0',
        },
      ],
      unknown: '#e3e8eb',
      field: 'geo_code_long',
      attribute: 'colors',
    });

    if (dimension) dimension.transform = id;
    else this.breakdown.dimensions.push({ dimension: 'fill', transform: id });
  }

  setGeoLegend() {
    this.breakdown.legend = { attributefield: 'GEO_NAME' };
  }

  orderByGeos([geo1, geo2]) {
    this.breakdown.sorts = this.breakdown.sorts || [];
    this.breakdown.sorts.push({
      field: 'geo_code_long',
      order: geo1 < geo2 ? 'asc' : 'desc',
    });
  }

  orderByData(order = 'desc') {
    this.breakdown.sorts = this.breakdown.sorts || [];
    this.breakdown.sorts.push({ field: this.breakdown.valueField, order });
  }

  orderByQuery() {
    const bandTransform = this.breakdown.transforms.find(
      (t) => t.type === 'band',
    );
    if (!bandTransform) return;
    const bandField = bandTransform.field.replace(/_label$/, '');

    // Create a lookup map to keep things O(1) when we sort
    const indexes = fromPairs(
      (this.breakdown.queryFilters[bandField] || []).map((value, index) => [
        value,
        index,
      ]),
    );

    const order =
      this.breakdown.sorts
        .find((s) => s.field === bandField)
        ?.order.toUpperCase() ?? 'ASC';

    // Override any sorts with a comparitor function that checks against
    // the index in the queryFilters list.
    this.breakdown.sorts = (a, b) => {
      const aIndex = indexes[a[bandField]];
      const bIndex = indexes[b[bandField]];
      if (aIndex === bIndex) return 0;
      if (order === 'ASC') return aIndex > bIndex ? 1 : -1;
      return bIndex > aIndex ? 1 : -1;
    };
  }

  orderAlphabetical() {
    const bandTransform = this.breakdown.transforms.find(
      (t) => t.type === 'band',
    );
    if (!bandTransform) return;
    const bandField = bandTransform.field.replace(/_label$/, '');
    const bandFieldLabel = bandTransform.field;
    // Create a lookup map to keep things O(1) when we sort
    const toOrderindexes = this.breakdown.transforms.find(
      (t) => t.attribute === 'labels' && t.field === bandField,
    ).values;

    const order =
      this.breakdown.sorts
        .find((s) => s.field === bandField || s.field === bandFieldLabel)
        ?.order.toUpperCase() ?? 'ASC';

    if (!toOrderindexes) return;

    const orderedIndexes = toOrderindexes.sort((a, b) => {
      const aValue = a.value;
      const bValue = b.value;
      if (aValue === bValue) return 0;
      if (order === 'ASC') return aValue > bValue ? 1 : -1;
      return bValue > aValue ? 1 : -1;
    });

    const indexes = fromPairs(
      orderedIndexes.map((value, index) => [value.key, index]),
    );

    // Override any sorts with a comparitor function that checks against
    // the ordered list
    this.breakdown.sorts = (a, b) => {
      const aIndex = indexes[a[bandField]];
      const bIndex = indexes[b[bandField]];
      if (aIndex === bIndex) return 0;
      if (order === 'ASC') return aIndex > bIndex ? 1 : -1;
      return bIndex > aIndex ? -1 : 1;
    };

    if (this.breakdown.legend?.attributefield) {
      const legendField = `${this.breakdown.legend.attributefield}_label`;
      this.breakdown.legendSorter = (a, b) => {
        const aIndex = a[legendField];
        const bIndex = b[legendField];
        if (aIndex === bIndex) return 0;
        if (order === 'ASC') return aIndex > bIndex ? 1 : -1;
        return bIndex > aIndex ? -1 : 1;
      };
    }
  }

  orderItems() {
    const bandTransform = this.breakdown.transforms.find(
      (t) => t.type === 'band',
    );
    if (!bandTransform) return;
    const bandField = bandTransform.field.replace(/_label$/, '');
    const order =
      this.breakdown.sorts
        .find((s) => s.field === bandField)
        ?.order.toUpperCase() ?? 'ASC';
    // Order the queryFilters list and create a index to be used on the sort function.
    const orderedIndexes =
      order.toUpperCase() === 'ASC'
        ? (this.breakdown.queryFilters[bandField] || []).sort()
        : (this.breakdown.queryFilters[bandField] || []).sort().reverse();
    const indexes = fromPairs(
      orderedIndexes.map((value, index) => [value, index]),
    );

    // Override any sorts with a comparitor function that checks against
    // the index in the sorted queryFilters list
    this.breakdown.sorts = (a, b) => {
      const aIndex = indexes[a[bandField]];
      const bIndex = indexes[b[bandField]];
      if (aIndex === bIndex) return 0;
      return aIndex > bIndex ? 1 : -1;
    };
  }

  addRankLabelFilters(geoCodes) {
    const geoCodesObj = geoCodes
      .filter((x) => x)
      .reduce((acc, code) => ({ ...acc, [code]: true }), {});

    this.breakdown.labelFilters = this.breakdown.labelFilters || [];
    this.breakdown.labelFilters.push(
      (datum, index, labels) =>
        !index ||
        index === labels.length - 1 ||
        geoCodesObj[datum.geo_code_long],
    );
  }

  addGeoTypeFilters() {
    const labels = {
      '01': 'United States',
      '02': 'States',
      '03': 'Regions',
      '04': 'Counties',
      '05': 'Regions',
      '06': 'Cities',
      '07': 'Cities, towns, and CDPs', // 'Places'
      '08': 'Tracts',
      '09': 'Sub-Counties',
    };

    // TODO: temporary, remove after indicators are re-saved
    this.breakdown.geoTypes = this.breakdown.geoTypes || [
      '04',
      '07',
      '09',
      '08',
    ];

    this.breakdown.geoTypeFilters = (this.breakdown.geoTypes || []).map(
      (id) => ({
        text: labels[id] || id,
        value: id,
      }),
    );
  }

  modifyChart(attributes, primaryGeo, secondaryGeo) {
    this.applyAttributeTransforms(attributes);
    if (this.breakdown.axes.length > 0) {
      if (this.breakdown.subtype === 'rank') {
        this.modifyRankChart(primaryGeo, secondaryGeo);
      } else if (this.breakdown.allowGeoCompare && secondaryGeo.id) {
        this.setGeoCompare(primaryGeo, secondaryGeo);
      }

      if ('orderByValue' in this.breakdown) {
        this.orderByData(this.breakdown.orderByValue);
        return;
      }
      if (this.breakdown.orderAlphabetical && !this.breakdown.orderByQuery) {
        // If order_alphabetical is TRUE and order_by_query is TRUE, sort according to order_by_query
        this.orderAlphabetical();
        return;
      }
      if (this.breakdown.orderByQuery === true) this.orderByQuery();
      if (
        !this.breakdown.orderByQuery &&
        this.breakdown.sorts &&
        this.breakdown.subtype !== 'rank'
      )
        this.orderItems();
    }
  }

  modifyRankChart(primaryGeo, secondaryGeo) {
    if (primaryGeo.id) {
      this.colorByGeoCode(primaryGeo.id, secondaryGeo.id);
      this.addRankLabelFilters([primaryGeo.id, secondaryGeo.id]);
      this.orderByData();
    }
  }

  setGeoCompare(primaryGeo, secondaryGeo) {
    this.colorByGeoName();
    this.setGeoLegend();
    this.orderByGeos([primaryGeo.id, secondaryGeo.id]);

    if (['bar', 'column', 'row'].includes(this.breakdown.subtype)) {
      this.bandByGeo();
    }
  }

  get({ attributes, primaryGeo, secondaryGeo }) {
    if (this.breakdown.subtype === 'map') this.addGeoTypeFilters();
    else this.modifyChart(attributes, primaryGeo, secondaryGeo);

    return this.breakdown;
  }
}
